import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X } from "lucide-react";

const CATEGORIES = ["Health", "Fitness", "Productivity", "Mind", "Finance", "Learning", "General"];
const COLORS = ["#E9E604", "#3B82F6", "#8B5CF6", "#F59E0B", "#FF5722", "#EC4899", "#C9C600"];

const HABIT_IDEAS = [
  { title: "10 min walk after dinner", category: "Fitness", type: "checkoff" },
  { title: "Drink water", category: "Health", type: "countable", target: 8, unit: "glasses" },
  { title: "Read", category: "Learning", type: "countable", target: 10, unit: "pages" },
  { title: "Lights out by 10:30", category: "Health", type: "checkoff" },
  { title: "Strength session", category: "Fitness", type: "checkoff" },
  { title: "5 portions of veg", category: "Health", type: "countable", target: 5, unit: "portions" },
  { title: "No phone first hour", category: "Mind", type: "checkoff" },
  { title: "Meditate", category: "Mind", type: "countable", target: 5, unit: "minutes" },
  { title: "Plan tomorrow tonight", category: "Productivity", type: "checkoff" },
  { title: "Get outside in daylight", category: "Mind", type: "checkoff" },
  { title: "Stretch / mobility", category: "Fitness", type: "checkoff" },
  { title: "Alcohol-free day", category: "Health", type: "checkoff" },
];

const empty = {
  title: "", description: "", category: "Health", type: "checkoff",
  target: 1, unit: "", color: "#10B981", reminder: "", icon: "target",
};

export default function HabitModal({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial } : empty);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave({
      ...form,
      target: form.type === "countable" ? Math.max(1, parseInt(form.target) || 1) : 1,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#12141C] border border-[#242417] text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">
            {initial ? "Edit Habit" : "New Habit"}
          </DialogTitle>
          <DialogDescription className="text-white">
            Set up your habit details, type, target and reminder.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 mt-2">
          {!initial && (
            <div>
              <label className="lbl">Quick ideas · start ridiculously small</label>
              <div className="flex gap-2 flex-wrap">
                {HABIT_IDEAS.map((idea) => (
                  <button
                    key={idea.title}
                    type="button"
                    data-testid={`habit-idea-${idea.title.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setForm((f) => ({ ...f, ...idea, description: f.description }))}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#33331f] text-white hover:border-emerald-500/50 hover:text-emerald-400 transition-colors"
                  >
                    {idea.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="lbl">Title</label>
            <input
              data-testid="habit-modal-title-input"
              autoFocus
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Drink water"
              className="fld"
            />
          </div>

          <div>
            <label className="lbl">Description</label>
            <input
              data-testid="habit-modal-desc-input"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Optional note"
              className="fld"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="lbl">Category</label>
              <select data-testid="habit-modal-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className="fld">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl">Reminder</label>
              <input
                data-testid="habit-modal-reminder-input"
                type="time"
                value={form.reminder}
                onChange={(e) => set("reminder", e.target.value)}
                className="fld"
              />
            </div>
          </div>

          <div>
            <label className="lbl">Type</label>
            <div className="grid grid-cols-2 gap-3">
              {[["checkoff", "Simple Check-off"], ["countable", "Countable Goal"]].map(([v, lbl]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`habit-type-${v}`}
                  onClick={() => set("type", v)}
                  className={`h-11 rounded-xl border font-medium transition-colors ${
                    form.type === v ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-[#33331f] text-white hover:bg-white/5"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {form.type === "countable" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="lbl">Target</label>
                <input
                  data-testid="habit-modal-target-input"
                  type="number" min="1"
                  value={form.target}
                  onChange={(e) => set("target", e.target.value)}
                  className="fld"
                />
              </div>
              <div>
                <label className="lbl">Unit</label>
                <input
                  data-testid="habit-modal-unit-input"
                  value={form.unit}
                  onChange={(e) => set("unit", e.target.value)}
                  placeholder="glasses, mins…"
                  className="fld"
                />
              </div>
            </div>
          )}

          <div>
            <label className="lbl">Color</label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set("color", c)}
                  className={`w-9 h-9 rounded-lg transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-offset-[#12141C] ring-white scale-110" : ""}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-[#33331f] text-white font-medium hover:bg-white/5">
              Cancel
            </button>
            <button
              data-testid="habit-modal-save-button"
              type="submit"
              className="flex-1 h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold hover:opacity-90"
            >
              {initial ? "Save Changes" : "Create Habit"}
            </button>
          </div>
        </form>

        <style>{`
          .lbl { display:block; font-size:.7rem; text-transform:uppercase; letter-spacing:.05em; color:#ffffff; margin-bottom:.4rem; font-weight:500; }
          .fld { width:100%; height:2.75rem; padding:0 .85rem; border-radius:.75rem; background:#0D0F17; border:1px solid rgba(30,41,59,.9); color:#FFFFFF; outline:none; }
          .fld:focus { border-color:#34d399; box-shadow:0 0 0 3px rgba(16,185,129,.15); }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
